import React, { useCallback } from 'react';
import {
  List,
  Card,
  Button,
  Progress,
  Image,
  Tag,
  Tooltip,
  Checkbox,
  Space,
  Typography,
  Popconfirm,
  Alert
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { FileInfo, FileStatus, FileListProps } from '../types';
import { FileUtils } from '../utils/fileUtils';
import './FileList.css';

const { Text } = Typography;

export const FileList: React.FC<FileListProps> = ({
  files,
  onFileRemove,
  onFilePreview,
  onFilesSelect,
  selectedFileIds
}) => {

  // 获取状态图标
  const getStatusIcon = (status: FileStatus, progress?: number) => {
    switch (status) {
      case FileStatus.PENDING:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case FileStatus.UPLOADING:
      case FileStatus.PROCESSING:
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
      case FileStatus.COMPLETED:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case FileStatus.ERROR:
        return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      case FileStatus.CANCELLED:
        return <ExclamationCircleOutlined style={{ color: '#8c8c8c' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  // 获取状态文本
  const getStatusText = (status: FileStatus, progress?: number) => {
    switch (status) {
      case FileStatus.PENDING:
        return '待处理';
      case FileStatus.UPLOADING:
        return '上传中';
      case FileStatus.PROCESSING:
        return `处理中 ${progress || 0}%`;
      case FileStatus.COMPLETED:
        return '已完成';
      case FileStatus.ERROR:
        return '处理失败';
      case FileStatus.CANCELLED:
        return '已取消';
      default:
        return '未知状态';
    }
  };

  // 获取状态颜色
  const getStatusColor = (status: FileStatus) => {
    switch (status) {
      case FileStatus.PENDING:
        return 'orange';
      case FileStatus.UPLOADING:
      case FileStatus.PROCESSING:
        return 'blue';
      case FileStatus.COMPLETED:
        return 'green';
      case FileStatus.ERROR:
        return 'red';
      case FileStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  // 处理文件选择
  const handleFileSelect = useCallback((fileId: string, checked: boolean) => {
    const newSelectedIds = checked
      ? [...selectedFileIds, fileId]
      : selectedFileIds.filter(id => id !== fileId);
    onFilesSelect(newSelectedIds);
  }, [selectedFileIds, onFilesSelect]);

  // 处理全选
  const handleSelectAll = useCallback((checked: boolean) => {
    const newSelectedIds = checked ? files.map(f => f.id) : [];
    onFilesSelect(newSelectedIds);
  }, [files, onFilesSelect]);

  // 处理预览
  const handlePreview = useCallback((fileId: string) => {
    onFilePreview(fileId);
  }, [onFilePreview]);

  // 处理下载
  const handleDownload = useCallback((fileInfo: FileInfo) => {
    if (fileInfo.processedData) {
      FileUtils.downloadFile(
        fileInfo.processedData.processedBlob,
        fileInfo.processedData.downloadName
      );
    }
  }, []);

  // 计算统计信息
  const getStatistics = () => {
    const total = files.length;
    const completed = files.filter(f => f.status === FileStatus.COMPLETED).length;
    const processing = files.filter(f =>
      f.status === FileStatus.PROCESSING || f.status === FileStatus.UPLOADING
    ).length;
    const errors = files.filter(f => f.status === FileStatus.ERROR).length;
    const pending = files.filter(f => f.status === FileStatus.PENDING).length;

    return { total, completed, processing, errors, pending };
  };

  const stats = getStatistics();
  const isAllSelected = files.length > 0 && selectedFileIds.length === files.length;
  const isIndeterminate = selectedFileIds.length > 0 && selectedFileIds.length < files.length;

  if (files.length === 0) {
    return (
      <div className="file-list-empty">
        <Text type="secondary">暂无文件，请先上传图片</Text>
      </div>
    );
  }

  return (
    <div className="file-list-container">
      {/* 统计信息和操作栏 */}
      <Card size="small" className="file-list-header">
        <div className="file-list-stats">
          <Space size="large">
            <Text>
              总数: <Text strong>{stats.total}</Text>
            </Text>
            {stats.completed > 0 && (
              <Text>
                已完成: <Text strong style={{ color: '#52c41a' }}>{stats.completed}</Text>
              </Text>
            )}
            {stats.processing > 0 && (
              <Text>
                处理中: <Text strong style={{ color: '#1890ff' }}>{stats.processing}</Text>
              </Text>
            )}
            {stats.errors > 0 && (
              <Text>
                失败: <Text strong style={{ color: '#f5222d' }}>{stats.errors}</Text>
              </Text>
            )}
          </Space>
        </div>

        <div className="file-list-actions">
          <Space>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={e => handleSelectAll(e.target.checked)}
            >
              全选
            </Checkbox>

            {selectedFileIds.length > 0 && (
              <Text type="secondary">已选择 {selectedFileIds.length} 项</Text>
            )}
          </Space>
        </div>
      </Card>

      {/* 文件列表 */}
      <List
        className="file-list"
        dataSource={files}
        renderItem={(fileInfo) => (
          <List.Item className="file-list-item">
            <Card
              size="small"
              className={`file-item-card ${
                selectedFileIds.includes(fileInfo.id) ? 'selected' : ''
              }`}
            >
              <div className="file-item-content">
                {/* 选择框 */}
                <div className="file-item-checkbox">
                  <Checkbox
                    checked={selectedFileIds.includes(fileInfo.id)}
                    onChange={e => handleFileSelect(fileInfo.id, e.target.checked)}
                  />
                </div>

                {/* 预览图 */}
                <div className="file-item-preview">
                  {fileInfo.preview ? (
                    <Image
                      src={fileInfo.preview}
                      alt={fileInfo.name}
                      width={60}
                      height={60}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                      preview={false}
                    />
                  ) : (
                    <div className="file-item-placeholder">
                      <EyeOutlined style={{ fontSize: 24, color: '#d9d9d9' }} />
                    </div>
                  )}
                </div>

                {/* 文件信息 */}
                <div className="file-item-info">
                  <div className="file-item-name">
                    <Tooltip title={fileInfo.name}>
                      <Text strong ellipsis style={{ maxWidth: 200 }}>
                        {fileInfo.name}
                      </Text>
                    </Tooltip>
                  </div>

                  <div className="file-item-meta">
                    <Space size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {FileUtils.formatFileSize(fileInfo.size)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {FileUtils.getFileTypeDescription(fileInfo.file)}
                      </Text>
                    </Space>
                  </div>

                  {/* 错误信息 */}
                  {fileInfo.error && (
                    <div className="file-item-error">
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {fileInfo.error.message}
                      </Text>
                    </div>
                  )}
                </div>

                {/* 状态和进度 */}
                <div className="file-item-status">
                  <div className="status-info">
                    <Tag
                      icon={getStatusIcon(fileInfo.status, fileInfo.progress)}
                      color={getStatusColor(fileInfo.status)}
                    >
                      {getStatusText(fileInfo.status, fileInfo.progress)}
                    </Tag>
                  </div>

                  {/* 进度条 */}
                  {(fileInfo.status === FileStatus.PROCESSING ||
                    fileInfo.status === FileStatus.UPLOADING) && (
                    <div className="progress-bar">
                      <Progress
                        percent={fileInfo.progress}
                        size="small"
                        showInfo={false}
                        strokeColor="#1890ff"
                      />
                    </div>
                  )}
                </div>

                {/* 操作按钮 */}
                <div className="file-item-actions">
                  <Space size="small">
                    <Tooltip title="预览">
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handlePreview(fileInfo.id)}
                      />
                    </Tooltip>

                    {fileInfo.status === FileStatus.COMPLETED && (
                      <Tooltip title="下载">
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload(fileInfo)}
                        />
                      </Tooltip>
                    )}

                    <Tooltip title="删除">
                      <Popconfirm
                        title="确认删除这个文件吗？"
                        onConfirm={() => onFileRemove(fileInfo.id)}
                        okText="删除"
                        cancelText="取消"
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Tooltip>
                  </Space>
                </div>
              </div>
            </Card>
          </List.Item>
        )}
      />

      {/* 错误汇总 */}
      {stats.errors > 0 && (
        <Alert
          message={`有 ${stats.errors} 个文件处理失败`}
          description="请检查文件格式和大小，或重试处理"
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </div>
  );
};